import differenceInYears from 'date-fns/differenceInYears';
import _every from 'lodash/every';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import { sha256 } from 'js-sha256';
import { getLocalData } from 'utils/localDataStore';
import { getSubdomain } from 'utils/stringUtils';
import {
  visibleIfRegex,
  visibleIfContainsRegex,
  visibleIfNotContainsRegex,
  visibleIfContainsWoBracketRegex,
  parenthesisRegex,
} from './constants';

export const getHashedUserId = userId => sha256(sha256(sha256(sha256(userId))));

const getAssessmentJSON = (
  assessment,
  lastAnswer,
  isRetake,
  profile,
  insuranceQuestionNames,
  excludedQuestions,
) => {
  const json = JSON.parse(assessment.json);
  if (isRetake && lastAnswer) {
    const newPages = json.pages.map(page => {
      const newElements = page.elements.map(element => ({
        ...element,
        defaultValue: lastAnswer.result[element.name],
      }));

      const newPage = page;
      newPage.elements = newElements;
      return newPage;
    });

    json.pages = newPages;
    return removeGenderAgeIndustryQuestions(
      json,
      profile,
      excludedQuestions,
      insuranceQuestionNames,
    );
  }

  return removeGenderAgeIndustryQuestions(
    json,
    profile,
    excludedQuestions,
    insuranceQuestionNames,
  );
};

const removeGenderAgeIndustryQuestions = (
  assessment,
  profile,
  excludedQuestions = [],
  insuranceQuestionNames = [],
) => {
  // TODO: Remove when month/year approach spreads
  if (profile.isLoaded) {
    if (excludedQuestions.length || !_isEmpty(profile.insurance)) {
      const pages = assessment.pages.map(page => {
        const newElements = page.elements.map(el => {
          if (
            excludedQuestions.includes(el.name) ||
            (!_isEmpty(profile.insurance) &&
              insuranceQuestionNames.includes(el.name))
          ) {
            const newElement = el;
            newElement.visible = false;

            return newElement;
          }

          const newElement = el;
          newElement.visible = true;

          return el;
        });

        if (newElements.filter(el => el.visible).length === 0) return null;

        return {
          ...page,
          elements: newElements,
        };
      });

      const newAssessment = assessment;
      newAssessment.pages = pages.filter(page => page !== null);

      return newAssessment;
    }
    return assessment;
  }
  return assessment;
};

const getDataFromResult = (result, name, customDemographics = {}) => {
  const data = result[name];

  if (customDemographics[name]) {
    const [answer] = customDemographics[name].choices.filter(
      choice => choice.value === data,
    );
    if (answer) return answer.readableValue;
  }

  return data || '';
};

const getCleanResult = result => {
  const cleanResult = { ...result };

  Object.keys(cleanResult).forEach(key => {
    if (key.includes('other-option/')) {
      const question = key.split('other-option/')[1];
      if (Array.isArray(cleanResult[question])) {
        cleanResult[question] = cleanResult[question]
          .concat(cleanResult[key])
          .filter(item => item !== 'other-option');
      } else {
        cleanResult[question] = cleanResult[key];
      }
      delete cleanResult[key];
    }
  });
  // delete cleanResult.genderStandard;
  // delete cleanResult.ageRangeStandard;
  // delete cleanResult['genderStandard-Comment'];

  return cleanResult;
};

const convertToPattern = (fieldName, value, siteConfig, clientShortName) => {
  // TODO: Remove when month/year approach spreads
  if (fieldName === 'dob' || fieldName === 'gender') {
    const config = _get(
      siteConfig,
      ['config', fieldName === 'dob' ? 'age' : fieldName],
      {},
    );
    const mapping = {};
    if (_get(config, clientShortName)) {
      _get(config, clientShortName, []).forEach(item => {
        mapping[item.answerValue] = item.value;
      });
    } else {
      _get(config, 'default', []).forEach(item => {
        mapping[item.answerValue] = item.value;
      });
    }
    return mapping[value] || value;
  }
  if (fieldName === 'industry') {
    switch (value) {
      case '1':
        return 'A student';
      case '2':
        return 'Working in healthcare';
      case '3':
        return 'Working in education or childcare';
      case '4':
        return 'Working in service, retail or gig economy';
      case '5':
        return 'Working in other private sector';
      case '6':
        return 'Working in other government';
      case '7':
        return 'Working in other non-profit';
      case '8':
        return 'Unemployed';
      case '9':
        return 'Retired';
      default:
        return value;
    }
  }
  if (fieldName === 'insurance') {
    const mapping = {};
    _get(siteConfig, 'config.options', []).forEach(item => {
      const clients = _get(item, 'client', []);
      if (clients.includes(clientShortName)) {
        mapping[item.answerValue] = item.value;
      }
    });
    if (Array.isArray(value)) {
      return value.map(item => mapping[item] || item);
    }
    return mapping[value] || value;
  }
  return null;
};

const getNumberOfQuestions = data => {
  let len = 0;
  data.pages.forEach(item => {
    len += _get(item, 'elements', []).length;
  });

  return len;
};

const getQuestionVisibility = (question, answers, isPage = false) => {
  if (question.visible === false) return false;
  if (!_has(question, 'visibleIf') || !question.visibleIf) return true;
  // Handle brandIdentifier conditions
  if (question.visibleIf.includes('{brandIdentifier}')) {
    const { visibleIf } = question;
    // Extract all question variables from the condition using regex
    const questionRegex = /\{([^}]+)\}/g;

    // Also extract variables used with single equals sign (which visibleIfRegex misses)
    const singleEqualsRegex = /\{([^}]+)\}\s*=\s*["']([^"']+)["']/g;

    const allVars = [];

    // Get all variables in curly braces
    let match = questionRegex.exec(question.visibleIf);
    while (match !== null) {
      const varName = match[1];
      if (varName !== 'brandIdentifier' && !allVars.includes(varName)) {
        allVars.push(varName);
      }
      match = questionRegex.exec(question.visibleIf);
    }

    // Also check for variables with single equals sign which might be missed
    let equalsMatch = singleEqualsRegex.exec(question.visibleIf);
    while (equalsMatch !== null) {
      const varName = equalsMatch[1];
      if (varName !== 'brandIdentifier' && !allVars.includes(varName)) {
        allVars.push(varName);
      }
      equalsMatch = singleEqualsRegex.exec(question.visibleIf);
    }

    // Check if all referenced questions have answers
    const allQuestionsHaveAnswers =
      allVars.length === 0 ||
      allVars.every(varName => _get(answers, varName) !== undefined);

    // For pages, we need at least one question to have an answer
    const atLeastOneQuestionHasAnswer =
      allVars.length > 0 &&
      allVars.some(varName => _get(answers, varName) !== undefined);

    // If questions don't have answers yet, don't show this question
    if (!allQuestionsHaveAnswers && !(isPage && atLeastOneQuestionHasAnswer)) {
      return isPage;
    }

    // Deal with brand condition
    const currentBrand = getSubdomain() || getLocalData('brand');
    // Check for simple brand conditions first
    const simpleBrandEquals = visibleIf.match(
      /\{brandIdentifier\}\s*=\s*['"]([^'"]+)['"]/,
    );
    const simpleBrandNotEquals = visibleIf.match(
      /\{brandIdentifier\}\s*!=\s*['"]([^'"]+)['"]/,
    );

    // If it's a simple condition (just equals or not equals), handle it directly
    if (
      simpleBrandEquals &&
      !visibleIf.includes('and') &&
      !visibleIf.includes('or')
    ) {
      return simpleBrandEquals[1] === currentBrand;
    }
    if (
      simpleBrandNotEquals &&
      !visibleIf.includes('and') &&
      !visibleIf.includes('or')
    ) {
      return simpleBrandNotEquals[1] !== currentBrand;
    }

    // For complex conditions, prepare the expression by replacing brandIdentifier with actual value
    const definitiveExpression = visibleIf
      .replace(/OR/g, 'or')
      .replace(/AND/g, 'and')
      .replace(/\{brandIdentifier\}\s*=\s*['"]([^'"]+)['"]/g, (_, brand) =>
        brand === currentBrand ? 'true' : 'false',
      )
      .replace(/\{brandIdentifier\}\s*!=\s*['"]([^'"]+)['"]/g, (_, brand) =>
        brand !== currentBrand ? 'true' : 'false',
      );

    // Parse and evaluate the expression
    const { parsedExpression, parsedAnswers } = recursivelyParseParenthesis(
      definitiveExpression,
      parseMonthYearDate(answers),
    );

    const result = getCompositeExpressionValue(
      parsedExpression,
      parsedAnswers,
      false,
      isPage,
    );

    return result;
  }

  // Original logic for non-brandIdentifier conditions
  const definitiveExpression = question.visibleIf
    .replace(/OR/g, 'or')
    .replace(/AND/g, 'and');
  const relatedAnswers = definitiveExpression.match(visibleIfRegex) || [];
  const relatedAnswersExist =
    relatedAnswers.length === 0 ||
    relatedAnswers.every(
      ans => _get(answers, ans.substring(1, ans.length - 1)) !== undefined,
    );
  const atLeastOneRelatedAnswerExist =
    relatedAnswers.length > 0 &&
    relatedAnswers.some(
      ans => _get(answers, ans.substring(1, ans.length - 1)) !== undefined,
    );
  if (relatedAnswersExist || atLeastOneRelatedAnswerExist) {
    const { parsedExpression, parsedAnswers } = recursivelyParseParenthesis(
      definitiveExpression,
      parseMonthYearDate(answers),
    );
    return getCompositeExpressionValue(
      parsedExpression,
      parsedAnswers,
      false,
      isPage,
    );
  }
  return isPage;
};

const parseMonthYearDate = answers => {
  if (!answers.ageMonthYearStandard) return answers;
  if (typeof answers.ageMonthYear !== 'string') return answers;

  const parsedAnswers = { ...answers };
  parsedAnswers.ageMonthYearStandard = differenceInYears(
    Date.now(),
    new Date(
      answers.ageMonthYearStandard.split('-')[0],
      answers.ageMonthYearStandard.split('-')[1],
    ),
  );
  return parsedAnswers;
};

const getResultsVisibility = (
  expression,
  answers,
  fallback = false,
  debug = false,
) => {
  if (expression === '') return fallback;

  const definitiveExpression = expression
    .replace(/OR/g, 'or')
    .replace(/AND/g, 'and');
  const relatedAnswers = definitiveExpression.match(visibleIfRegex);
  const relatedAnswersExist = relatedAnswers.some(
    ans => _get(answers, ans.substring(1, ans.length - 1)) !== undefined,
  );

  if (relatedAnswersExist) {
    const { parsedExpression, parsedAnswers } = recursivelyParseParenthesis(
      definitiveExpression,
      parseMonthYearDate(answers),
      debug,
    );
    if (debug)
      console.log('getResultsVisibility', { parsedExpression, parsedAnswers });
    return getCompositeExpressionValue(parsedExpression, parsedAnswers, debug);
  }
  return fallback;
};

const getAssessmentsResultsVisibility = (expression, answers) => {
  const containsRegex = /contains\(\[([^\]]+)\], \[([^\]]+)\]\)/g;

  let parsedExpression = expression.replace(
    containsRegex,
    (match, listKey, valueKey) => {
      const listValue = answers[listKey];
      const targetValue = answers[valueKey];

      if (!listValue || !targetValue) return 'false';

      const list = Array.isArray(listValue)
        ? listValue
        : String(listValue).split(',');

      return list.includes(String(targetValue)) ? 'true' : 'false';
    },
  );

  parsedExpression = parsedExpression.replace(
    /\[([^\[\]]+)\]/g,
    (match, variable) => {
      if (variable.includes('[')) {
        const [matrixKey, innerKey] = variable.split(/\[|\]/).filter(Boolean);
        return answers[matrixKey] && answers[matrixKey][innerKey] !== undefined
          ? answers[matrixKey][innerKey]
          : '0';
      }
      return answers[variable] !== undefined ? answers[variable] : '0';
    },
  );

  try {
    // eslint-disable-next-line no-new-func
    const result = new Function(`"use strict"; return (${parsedExpression})`)();
    return Boolean(result);
  } catch (e) {
    return false;
  }
};

function recursivelyParseParenthesis(expression, answers, debug) {
  let count = 0;
  let parsedExpression = expression;
  let parenthesisExpressions = (
    parsedExpression.match(parenthesisRegex) || []
  ).map(expr => expr.substring(1, expr.length - 1));
  const parsedAnswers = { ...answers };
  while (!_isEmpty(parenthesisExpressions)) {
    if (debug)
      console.log('recursivelyParseParenthesis_0', {
        parenthesisExpressions,
        parsedExpression,
      });
    const parenthesisValues = parenthesisExpressions.map(expr =>
      getCompositeExpressionValue(expr, parsedAnswers, debug),
    );
    if (debug)
      console.log('recursivelyParseParenthesis_1', {
        parenthesisValues,
        parsedExpression,
      });
    for (let p = 0; p < parenthesisExpressions.length; p += 1) {
      parsedExpression = parsedExpression.replace(
        `(${parenthesisExpressions[p]})`,
        `{parenthesis${count}} = '1'`,
      );
      parsedAnswers[`parenthesis${count}`] = parenthesisValues[p] ? '1' : '0';
      count += 1;
    }

    parenthesisExpressions = (
      parsedExpression.match(parenthesisRegex) || []
    ).map(expr => expr.substring(1, expr.length - 1));
  }

  return {
    parsedExpression,
    parsedAnswers,
  };
}

function getCompositeExpressionValue(
  expression,
  answers,
  debug,
  isPage = false,
) {
  const valuesMap = {};
  const values = expression.match(/(?:['"])(?:[^'"]+)(?:['"])/g);
  let convertedExpression = expression;
  if (values && values.length > 0) {
    values.forEach(value => {
      const key = Math.floor((1 + Math.random()) * 0x1000000)
        .toString(16)
        .substring(1);
      valuesMap[key] = value.substring(1, value.length - 1);

      convertedExpression = convertedExpression.replace(
        new RegExp(value, 'g'),
        `"${key}"`,
      );
    });
  }

  const pieces = convertedExpression.split(/(\s+or\s+|\s+and\s+)/g);
  if (pieces.length === 1) {
    return getExpressionValue(pieces[0], answers, valuesMap, debug, isPage);
  }
  const conditions = pieces.map(piece =>
    getExpressionValue(piece, answers, valuesMap, debug, isPage),
  );
  if (debug)
    console.log('getCompositeExpressionValue_0', {
      pieces,
      valuesMap,
      conditions,
    });
  let op = null;
  let result = null;
  for (let i = 0; i < conditions.length; i += 1) {
    if (i === 0) result = conditions[i];
    else if (typeof conditions[i] === 'string') {
      if (conditions[i].includes('or')) {
        op = 'or';
      } else if (conditions[i].includes('and')) {
        op = 'and';
      }
    } else if (typeof conditions[i] === 'boolean') {
      if (op === 'or') {
        result = result || conditions[i];
        op = null;
      } else if (op === 'and') {
        result = result && conditions[i];
        op = null;
      }
    }
  }
  if (debug) console.log('getCompositeExpressionValue_1', { result });
  return result;
}

function getExpressionValue(
  expression,
  answers,
  valuesMap = {},
  debug,
  isPage = false,
) {
  if (debug) console.log('getExpressionValue_0', { expression, answers });
  if (expression.includes(' or ') || expression.includes(' and '))
    return expression;

  // Handle true/false literals from brand condition evaluation
  if (expression.trim() === 'true') return true;
  if (expression.trim() === 'false') return false;

  const relatedAnswers = expression.match(visibleIfRegex);
  if (!relatedAnswers || !relatedAnswers.length) {
    console.log('Debug - No related answers found for expression:', expression);
    return isPage;
  }

  const match = expression.match(/(\[.*\])/g);
  if (debug)
    console.log('getExpressionValue_1', { expression, relatedAnswers, match });

  if (
    !_get(answers, relatedAnswers[0].substring(1, relatedAnswers[0].length - 1))
  )
    return isPage;
  if (
    !expression.includes('contains') &&
    !expression.includes('does not contain') &&
    match &&
    match.length > 0
  ) {
    const possibleValues = match[0]
      .substring(1, match[0].length - 1)
      .replace(/"/g, '')
      .split(',');
    return _some(possibleValues, value =>
      _get(
        answers,
        relatedAnswers[0].substring(1, relatedAnswers[0].length - 1),
      ).includes(valuesMap[value]),
    );
  }

  if (expression.includes('>=')) {
    const expressionValue = expression.split(' >= ')[1].replace(/["']/g, '');
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) >= Number(valuesMap[expressionValue] || expressionValue)
    );
  }
  if (expression.includes('<=')) {
    const expressionValue = expression.split(' <= ')[1].replace(/["']/g, '');
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) <= Number(valuesMap[expressionValue] || expressionValue)
    );
  }
  if (expression.includes('=')) {
    let expressionValue = expression.split(' = ')[1].replace(/["']/g, '');
    if (expressionValue === 'true') {
      expressionValue = '1';
    } else if (expressionValue === 'false') {
      expressionValue = '0';
    }
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) === Number(valuesMap[expressionValue] || expressionValue)
    );
  }
  if (expression.includes('<>')) {
    let expressionValue = expression.split(' <> ')[1].replace(/["']/g, '');
    if (expressionValue === 'true') {
      expressionValue = '1';
    } else if (expressionValue === 'false') {
      expressionValue = '0';
    }
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) !== Number(valuesMap[expressionValue] || expressionValue)
    );
  }
  if (expression.includes('>')) {
    const expressionValue = expression.split(' > ')[1].replace(/["']/g, '');
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) > Number(valuesMap[expressionValue] || expressionValue)
    );
  }
  if (expression.includes('<')) {
    const expressionValue = expression.split(' < ')[1].replace(/["']/g, '');
    return (
      relatedAnswers.reduce(
        (acc, value) =>
          acc + Number(_get(answers, value.substring(1, value.length - 1))),
        0,
      ) < Number(valuesMap[expressionValue] || expressionValue)
    );
  }
  if (visibleIfContainsRegex.test(expression)) {
    visibleIfContainsRegex.lastIndex = 0;
    const elementsString = expression.split(' contains ')[1];
    const elementsValues = elementsString
      .substring(1, elementsString.length - 1)
      .split(',')
      .map(value => value.substring(1, value.length - 1));
    if (debug)
      console.log('visibleIfContains', {
        relatedAnswers,
        elementsString,
        elementsValues,
      });
    return _every(elementsValues, value =>
      _get(
        answers,
        relatedAnswers[0].substring(1, relatedAnswers[0].length - 1),
      ).includes(valuesMap[value] || value),
    );
  }
  if (visibleIfNotContainsRegex.test(expression)) {
    visibleIfNotContainsRegex.lastIndex = 0;
    const elementsString = expression.split(' does not contain ')[1];
    const elementsValues = elementsString
      .substring(1, elementsString.length - 1)
      .split(',')
      .map(value => value.substring(1, value.length - 1));
    if (debug)
      console.log('visibleIfNotContains', {
        relatedAnswers,
        elementsString,
        elementsValues,
      });
    return !_every(elementsValues, value =>
      _get(
        answers,
        relatedAnswers[0].substring(1, relatedAnswers[0].length - 1),
      ).includes(valuesMap[value] || value),
    );
  }
  if (visibleIfContainsWoBracketRegex.test(expression)) {
    visibleIfContainsWoBracketRegex.lastIndex = 0;
    const elementsString = expression.split(' contains ')[1];
    const elementsValues = elementsString
      .split(',')
      .map(value => value.substring(1, value.length - 1));
    if (debug)
      console.log('visibleIfContainsWoBracketRegex', {
        elementsString,
        elementsValues,
      });
    return _every(elementsValues, value =>
      _get(
        answers,
        relatedAnswers[0].substring(1, relatedAnswers[0].length - 1),
      ).includes(valuesMap[value] || value),
    );
  }
  if (relatedAnswers[0].includes('parenthesis')) {
    return answers[relatedAnswers[0]];
  }
  return true;
}

const getAssessmentReferrals = (referrals, clientShortName, assessmentId) => {
  const clientAssessmentReferral = referrals.filter(r => {
    const clientMatches = _get(r, 'clientCollection.items', []).some(
      item => _get(item, 'shortName') === clientShortName,
    );
    if (!clientMatches) {
      return false;
    }
    return _get(r, 'showCollection.items', []).some(
      item => _get(item, 'sys.id') === assessmentId,
    );
  });
  const defaultAssessmentReferral = referrals.find(r => {
    if (r.slug !== 'default-assessment-under-age') {
      return false;
    }

    if (!_isEmpty(_get(r, 'clientCollection.items', []))) {
      return _get(r, 'clientCollection.items').some(
        item => _get(item, 'shortName') === clientShortName,
      );
    }

    if (_isEmpty(_get(r, 'excludeClientCollection.items', []))) {
      return false;
    }

    return !_get(r, 'excludeClientCollection.items', []).some(
      c => _get(c, 'shortName') === clientShortName,
    );
  });

  return clientAssessmentReferral.concat(defaultAssessmentReferral);
};

const getAssessmentExcludedQuestions = (
  profile,
  profileFields,
  ageGenderMappingSiteConfig,
) => {
  const profileFieldsMapping = _get(
    ageGenderMappingSiteConfig,
    'pageCopy.profileFieldToQuestionName',
  ) || {
    birthdayMonthYear: ['ageMonthYearStandard'],
    dob: ['ageRangeStandard'],
    gender: ['genderStandard', 'genderLGBTQStandard'],
    industry: ['industryStandard'],
    stateOfResidence: ['stateOfResidence'],
  };

  const fieldsToCheck = profileFields || Object.keys(profileFieldsMapping);
  return Object.keys(profileFieldsMapping)
    .flatMap(key => {
      if (profile[key] || !fieldsToCheck.includes(key)) {
        return profileFieldsMapping[key];
      }

      return null;
    })
    .filter(Boolean);
};

export const getScorePercentage = (
  { maxValue, minValue, valueDirection },
  overallScoring,
) => {
  if (valueDirection === 'higherBetter') {
    return (overallScoring / (maxValue - minValue)) * 100;
  }

  return ((maxValue - minValue - overallScoring) / maxValue) * 100;
};

const getAssessmentName = (name, customNames = [], clientShortName) => {
  const availableCustomName = customNames.find(({ clients }) =>
    clients?.includes(clientShortName),
  );
  return availableCustomName ? availableCustomName.value : name;
};

export {
  getAssessmentJSON,
  removeGenderAgeIndustryQuestions,
  getDataFromResult,
  getCleanResult,
  convertToPattern,
  getNumberOfQuestions,
  getQuestionVisibility,
  getResultsVisibility,
  getAssessmentsResultsVisibility,
  getAssessmentReferrals,
  getAssessmentExcludedQuestions,
  getAssessmentName,
};

// Evaluate row visibility for matrix questions
export const getMatrixRowVisibility = (row, answers = {}) => {
  // If row doesn't have visibility condition, it's visible
  if (!row.visibleIf) return true;

  // If row has brand identifier condition, evaluate it
  if (row.visibleIf.includes('{brandIdentifier}')) {
    const currentBrand = getSubdomain() || getLocalData('brand');

    // Check for simple brand conditions first
    const simpleBrandEquals = row.visibleIf.match(
      /\{brandIdentifier\}\s*=\s*['"]([^'"]+)['"]/,
    );
    const simpleBrandNotEquals = row.visibleIf.match(
      /\{brandIdentifier\}\s*!=\s*['"]([^'"]+)['"]/,
    );

    // If it's a simple condition (just equals or not equals), handle it directly
    if (
      simpleBrandEquals &&
      !row.visibleIf.includes('and') &&
      !row.visibleIf.includes('or')
    ) {
      const isVisible = simpleBrandEquals[1] === currentBrand;

      return isVisible;
    }
    if (
      simpleBrandNotEquals &&
      !row.visibleIf.includes('and') &&
      !row.visibleIf.includes('or')
    ) {
      const isVisible = simpleBrandNotEquals[1] !== currentBrand;

      return isVisible;
    }

    // For complex conditions, prepare the expression
    const definitiveExpression = row.visibleIf
      .replace(/OR/g, 'or')
      .replace(/AND/g, 'and')
      .replace(/\{brandIdentifier\}\s*=\s*['"]([^'"]+)['"]/g, (_, brand) =>
        brand === currentBrand ? 'true' : 'false',
      )
      .replace(/\{brandIdentifier\}\s*!=\s*['"]([^'"]+)['"]/g, (_, brand) =>
        brand !== currentBrand ? 'true' : 'false',
      );

    // Parse and evaluate the expression
    try {
      const { parsedExpression, parsedAnswers } = recursivelyParseParenthesis(
        definitiveExpression,
        parseMonthYearDate(answers),
      );

      const isVisible = getCompositeExpressionValue(
        parsedExpression,
        parsedAnswers,
        false,
        false,
      );

      return isVisible;
    } catch (error) {
      console.error('Error evaluating brand condition:', error);
      // Default to showing the row if there's an error
      return true;
    }
  }

  // For non-brand conditions, use the regular expression evaluation
  try {
    const { parsedExpression, parsedAnswers } = recursivelyParseParenthesis(
      row.visibleIf,
      parseMonthYearDate(answers),
    );

    return getCompositeExpressionValue(
      parsedExpression,
      parsedAnswers,
      false,
      false,
    );
  } catch (error) {
    console.error('Error evaluating condition:', error);
    // Default to showing the row if there's an error
    return true;
  }
};

// Handle matrix questions with rowsVisibleIf conditions
export const processMatrixQuestion = (question, answers = {}) => {
  // Clone the question to avoid modifying the original
  const processedQuestion = { ...question };
  // Track hidden rows to default their answers
  const hiddenRows = [];

  // If the question has a rowsVisibleIf property, apply it
  if (question.rowsVisibleIf) {
    // Filter rows based on visibility conditions
    if (Array.isArray(processedQuestion.rows)) {
      // Apply rowsVisibleIf to each row that doesn't have its own visibleIf
      processedQuestion.rows = processedQuestion.rows
        .map(row => {
          // If row already has visibleIf, don't override it
          if (!row.visibleIf) {
            const visible = getMatrixRowVisibility(
              { ...row, visibleIf: processedQuestion.rowsVisibleIf },
              answers,
            );

            // Track hidden rows
            if (!visible && row.value) {
              hiddenRows.push(row.value);
            }

            return {
              ...row,
              visible,
            };
          }
          // For rows with their own visibleIf, evaluate that
          const visible = getMatrixRowVisibility(row, answers);

          // Track hidden rows
          if (!visible && row.value) {
            hiddenRows.push(row.value);
          }

          return {
            ...row,
            visible,
          };
        })
        .filter(row => row.visible !== false);
    }
  }
  // If no rowsVisibleIf, process individual row conditions
  else if (Array.isArray(question.rows)) {
    // Filter rows based on individual visibility conditions
    processedQuestion.rows = processedQuestion.rows
      .map(row => {
        const visible = row.visibleIf
          ? getMatrixRowVisibility(row, answers)
          : true;

        // Track hidden rows
        if (!visible && row.value) {
          hiddenRows.push(row.value);
        }

        return {
          ...row,
          visible,
        };
      })
      .filter(row => row.visible !== false);
  }

  // Default answers for hidden rows to empty strings if we have answer data
  if (hiddenRows.length > 0 && question.name && answers[question.name]) {
    // Create shallow copy of the answers
    const updatedAnswers = { ...answers };

    // Handle different matrix question types
    if (question.type === 'matrix') {
      // For simple matrix questions, ensure each hidden row has a default answer
      hiddenRows.forEach(rowValue => {
        if (!updatedAnswers[question.name][rowValue]) {
          if (!updatedAnswers[question.name]) {
            updatedAnswers[question.name] = {};
          }
          updatedAnswers[question.name][rowValue] = '';
        }
      });
    } else if (question.type === 'matrixdropdown') {
      // For matrix dropdown questions, set empty objects for each hidden row
      hiddenRows.forEach(rowValue => {
        if (!updatedAnswers[question.name][rowValue]) {
          if (!updatedAnswers[question.name]) {
            updatedAnswers[question.name] = {};
          }
          updatedAnswers[question.name][rowValue] = {};

          // If columns exist, set empty string for each column
          if (Array.isArray(question.columns)) {
            question.columns.forEach(col => {
              updatedAnswers[question.name][rowValue][col.name || col.value] =
                '';
            });
          }
        }
      });
    }

    // Update the answers reference (if mutable)
    Object.assign(answers, updatedAnswers);
  }

  return processedQuestion;
};
